import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import Warning from '@/images/WarningColored.svg';
import { PROJECT_IDB_DRAFT_QUERY_KEY, useProjectIdb } from '@/pages/AdvancedEditor/hooks/polotno-store/store-init/useProjectIdb';
import { newProjectId } from '@/pages/AdvancedEditor/types';
import { libraryItemToDelete } from '@/pages/video-library/components/VideoOptionsList';
import { useAnalytics } from '@/services/analytics';
import { useDeleteItemFromLibraryMutation } from '@/state/video-library/videoLibrary';
import { StyleCollection } from '@/utils/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useRecoilState } from 'recoil';
import { isDraft } from '../utils';

const Infographic = styled.img`
    width: 2.4rem;
    height: 2.4rem;
    margin-inline-end: 1rem;
`;

const ImageContainer = styled.div`
    width: 100%;
    height: auto;
    aspect-ratio: 2/1;
    min-height: 5rem;

    & img {
        border-radius: 8px;
        object-fit: contain;
    }
`;

const Image = styled.img`
    width: 100%;
    height: 100%;
`;

const styles: StyleCollection = {
    deleteButton: _theme => ({
        height: '4.4rem',
        padding: '0.5rem 2rem',
        fontSize: '1.6rem',
        alignItems: 'flex-center',
        flex: 1,
        borderRadius: '8px',
    }),
    closeIcon: theme => ({
        marginLeft: 'auto',
        color: theme.colors.primary,
    }),
    contentStyle: {
        flex: 1,
        overflow: 'hidden',
        alignItems: 'center',
    },
    cancelButton: {
        height: '4.4rem',
        padding: '0.5rem 2rem',
        fontSize: '1.6rem',
        alignItems: 'flex-start',
        borderRadius: '8px',
        flex: 1,
    },
};

const DialogSubtitle = styled(Typography)`
    line-height: 2.4rem;
    width: 100%;
    margin-bottom: 2rem;

    & span {
        text-decoration: underline;
        font-weight: bold;
    }
`;

export const DeleteLibraryItemModal = () => {
    const mixpanel = useAnalytics();
    const { mutateAsync: deleteItem, isLoading } = useDeleteItemFromLibraryMutation();
    const [itemToDelete, setItemToDelete] = useRecoilState(libraryItemToDelete);
    const { clearIdbDraft } = useProjectIdb();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (itemToDelete) {
            mixpanel?.track(`delete-video`, { event: 'view' });
        }
    }, [itemToDelete]);

    const handleDelete = async () => {
        if (itemToDelete) {
            mixpanel?.track('delete-video', {
                event: 'click',
                from: 'library',
            });

            if (itemToDelete.id === newProjectId) {
                await clearIdbDraft();
                setItemToDelete(null);
                queryClient.invalidateQueries({ queryKey: PROJECT_IDB_DRAFT_QUERY_KEY });
                return;
            }

            await deleteItem({ videoId: itemToDelete.id, type: itemToDelete.type });
            setItemToDelete(null);
        }
    };

    const handleClose = () => {
        setItemToDelete(null);
    };

    const itemType = !!itemToDelete && isDraft(itemToDelete) ? 'draft' : 'video';

    return (
        <BootstrapDialog
            onClose={handleClose}
            open={!!itemToDelete}
            width="50rem"
            contentstyle={styles.contentStyle}
            PaperProps={{ style: { borderRadius: '12px' } }}>
            <DialogTitle id="delete-image-modal">
                <Infographic src={Warning} />
                <Typography variant="h2">Confirm delete</Typography>
                <IconButton ariaLabel="close" onClick={handleClose} sx={styles.closeIcon}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <DialogSubtitle variant="body1">{`Are you sure you want to delete this ${itemType} from your gallery?`}</DialogSubtitle>
                {itemToDelete?.thumbnail_url && (
                    <ImageContainer>
                        <Image src={itemToDelete?.thumbnail_url ?? ''} />
                    </ImageContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button aria-label="cancel" sx={styles.cancelButton} onClick={handleClose} variant="outlined" color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleDelete} aria-label="delete" color="error" sx={styles.deleteButton} isLoading={isLoading}>
                    {`Delete ${itemType}`}
                </Button>
            </DialogActions>
        </BootstrapDialog>
    );
};
